import { Button, Card, Field, Notification, Panel, Select, useConfirm, useTheme } from '@hgc/ui';
import type { ThemePreference } from '@hgc/ui';
import type { UnitSystem } from '@hgc/core';
import { useCallback, useEffect, useState, type JSX, type MouseEvent } from 'react';

import {
  describeStoredLink,
  type AppSettingsStore,
  type ClearResult,
  type LocalDataCounts,
  type StorageNotice,
} from './settingsStore';
import { useAppSettingsStore } from './useAppSettingsStore';

/**
 * 应用设置。
 *
 * 三条产品约束在这里落成代码：
 *   1. **外观只用语义令牌**：按钮不给任何颜色类名，只有选中态（`aria-pressed`）——
 *      深浅色与高对比度由 `@hgc/ui` 的令牌决定。
 *   2. **不可逆的清空必须二次确认**，且确认强度与不可逆性匹配（`danger="critical"` +
 *      逐字输入短语），因为删掉的参数快照与审计日志**找不回来**。
 *   3. **存储不可用时直说**：隐私模式下"已保存"是假的，界面会明确显示"本次不会保存"。
 */

export interface AppSettingsPanelProps {
  readonly store: AppSettingsStore | null;
  readonly counts: LocalDataCounts | null;
  readonly onRefreshCounts: () => Promise<void>;
}

const THEME_OPTIONS: ReadonlyArray<readonly [ThemePreference, string]> = [
  ['light', '浅色'],
  ['dark', '深色'],
  ['system', '跟随系统'],
];

const UNIT_OPTIONS: ReadonlyArray<readonly [UnitSystem, string]> = [
  ['metric', '公制（m、m/s、km/h）'],
  ['imperial', '英制（ft、ft/s、mph）'],
];

/** 清空操作的确认短语。逐字输入它是"我确认要删"与"手滑"之间唯一的区分 */
const CLEAR_PHRASE = '清空';

export function AppSettingsPanel({
  store,
  counts,
  onRefreshCounts,
}: AppSettingsPanelProps): JSX.Element {
  const { preference, setPreference, contrast, setContrast } = useTheme();
  const confirm = useConfirm();

  const [units, setUnits] = useState<UnitSystem>(store?.current.units ?? 'metric');
  const [notice, setNotice] = useState<StorageNotice | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // 存储在挂载之后才打开，所以已保存的单位要在这里补上，
  // 否则界面会显示默认的"公制"而实际存的是"英制" —— 那就是两份真相
  useEffect(() => {
    if (store !== null) setUnits(store.current.units);
  }, [store]);

  const runClear = useCallback(
    async (
      key: 'params' | 'preflight' | 'audit',
      label: string,
      action: (target: AppSettingsStore) => Promise<ClearResult>,
      anchor: Element | null,
    ) => {
      const target = store;
      if (target === null) return;

      const amount = counts === null ? null : countOf(counts, key);
      const accepted = await confirm({
        title: `清空${label}？`,
        consequence:
          `会从本地存储里永久删除${label}${amount === null ? '' : `（当前 ${amount} 条）`}，` +
          '删除后无法恢复，也没有回收站。',
        confirmLabel: `清空${label}`,
        danger: 'critical',
        confirmPhrase: CLEAR_PHRASE,
        anchor,
        detail: (
          <p className="m-0 text-xs">
            这些数据只在本机：参数快照用于参数对比与恢复备份，飞前检查记录与审计日志用于事后追溯
            （"这次解锁前检查过什么、谁改过哪个参数"）。
          </p>
        ),
      });
      if (!accepted) return;

      setBusy(key);
      try {
        const outcome = await action(target);
        setNotice(outcome.notice);
        setResult(
          outcome.ok
            ? `已清空${label}：删除 ${outcome.removed} 条。`
            : `清空${label}失败：${outcome.notice?.message ?? '未知原因'}`,
        );
        await onRefreshCounts();
      } finally {
        setBusy(null);
      }
    },
    [confirm, counts, onRefreshCounts, store],
  );

  const handleUnits = useCallback(
    async (next: UnitSystem) => {
      setUnits(next);
      if (store === null) return;
      const outcome = await store.setUnits(next);
      setNotice(outcome);
      setResult(outcome === null ? `单位偏好已保存：${next === 'metric' ? '公制' : '英制'}` : null);
    },
    [store],
  );

  const handleForgetLastLink = useCallback(async () => {
    if (store === null) return;
    const outcome = await store.forgetLastLink();
    setNotice(outcome);
    setResult(outcome === null ? '已忘记上次使用的链路。' : null);
  }, [store]);

  return (
    <div className="flex flex-col gap-3">
      <Panel title="应用设置" description="外观、单位、链路默认值与本地数据。">
        <div className="flex flex-col gap-3">
          <Card title="外观" subtitle="只影响本机界面。深浅色与高对比度都走语义令牌，不写死颜色。">
            <div className="flex flex-col gap-2">
              <div
                className="flex w-fit items-center gap-0.5 rounded-control border border-line bg-surface-2 p-0.5"
                role="group"
                aria-label="外观偏好"
              >
                {THEME_OPTIONS.map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={preference === value}
                    onClick={() => setPreference(value)}
                    className="rounded-[calc(var(--radius-control)-2px)] px-2 py-0.5 text-xs text-fg-muted transition-colors aria-pressed:bg-surface-1 aria-pressed:text-fg"
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div>
                <Button
                  size="sm"
                  variant={contrast === 'high' ? 'primary' : 'secondary'}
                  aria-pressed={contrast === 'high'}
                  onClick={() => setContrast(contrast === 'high' ? 'normal' : 'high')}
                >
                  高对比度：{contrast === 'high' ? '已开启' : '已关闭'}
                </Button>
              </div>

              <p className="m-0 text-xs text-fg-subtle">
                外观偏好由界面层直接存进浏览器本地并立即生效；应用设置记录里也有一份同名副本，
                但它**不是**权威来源。将来合并成一处之前，不要依赖那一份。
              </p>
            </div>
          </Card>

          <Card title="单位" subtitle="影响读数与输入的单位换算。">
            <div className="w-80">
              <Field
                label="单位制"
                hint="会保存到本地设置。飞行/计划等页面的读数换算尚未接入这个字段（当前各处仍按公制显示）。"
              >
                <Select
                  value={units}
                  onChange={(value) => void handleUnits(value === 'imperial' ? 'imperial' : 'metric')}
                  options={UNIT_OPTIONS.map(([value, label]) => ({ value, label }))}
                />
              </Field>
            </div>
          </Card>

          <Card title="链路默认值" subtitle="连接页会用这里记下的链路预填表单。">
            <div className="flex flex-col gap-2">
              <p className="m-0 text-xs text-fg">
                上次使用的链路：
                <span className="ml-1 font-medium">{describeStoredLink(store?.lastLink ?? null)}</span>
              </p>
              <div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={store === null || store.lastLink === null}
                  onClick={() => void handleForgetLastLink()}
                >
                  忘记这条链路
                </Button>
              </div>
            </div>
          </Card>

          <Card
            title="本地数据"
            subtitle="只在本机保存。清空是不可逆的，每一条都要逐字输入确认短语。"
          >
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-xs text-fg">
              <li>参数快照：{counts?.paramSnapshots ?? '…'} 份</li>
              <li>飞前检查记录：{counts?.preflightRecords ?? '…'} 条</li>
              <li>审计日志：{counts?.auditEntries ?? '…'} 条</li>
            </ul>

            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="danger"
                loading={busy === 'params'}
                disabled={store === null}
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  void runClear('params', '参数快照', (target) => target.clearParamSnapshots(), event.currentTarget)
                }
              >
                清空参数快照
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={busy === 'preflight'}
                disabled={store === null}
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  void runClear(
                    'preflight',
                    '飞前检查记录',
                    (target) => target.clearPreflightRecords(),
                    event.currentTarget,
                  )
                }
              >
                清空飞前检查记录
              </Button>
              <Button
                size="sm"
                variant="danger"
                loading={busy === 'audit'}
                disabled={store === null}
                onClick={(event: MouseEvent<HTMLButtonElement>) =>
                  void runClear('audit', '审计日志', (target) => target.clearAuditLog(), event.currentTarget)
                }
              >
                清空审计日志
              </Button>
            </div>
          </Card>

          {store === null ? (
            <Notification level="info" title="正在打开本地存储…">
              存储的可用性会在打开之后确定；不可用时这一页会明确说明哪些改动不会被保存。
            </Notification>
          ) : null}

          {store?.readFailureNotice == null ? null : (
            <Notification level="critical" title="读取应用设置失败，当前显示的是默认值">
              {store.readFailureNotice.message}
              {store.readFailureNotice.advice === null ? null : ` ${store.readFailureNotice.advice}`}
            </Notification>
          )}

          {notice === null ? null : (
            <Notification
              level={levelOfNotice(notice)}
              title={notice.level === 'error' ? '本地数据操作失败' : '本次改动不会被保存'}
              onDismiss={() => setNotice(null)}
            >
              {notice.message}
              {notice.advice === null ? null : ` ${notice.advice}`}
            </Notification>
          )}

          {result === null ? null : (
            <Notification level="ok" onDismiss={() => setResult(null)}>
              {result}
            </Notification>
          )}
        </div>
      </Panel>
    </div>
  );
}

function countOf(counts: LocalDataCounts, key: 'params' | 'preflight' | 'audit'): number {
  if (key === 'params') return counts.paramSnapshots;
  if (key === 'preflight') return counts.preflightRecords;
  return counts.auditEntries;
}

/** 存储提示的等级 → 语义等级：失败用 critical（要立刻看见），"存不下"用 warning */
function levelOfNotice(notice: StorageNotice): 'critical' | 'warning' {
  return notice.level === 'error' ? 'critical' : 'warning';
}

/**
 * 设置页容器：自己打开存储，再交给面板。
 *
 * 拆成两个组件是为了让面板保持"纯展示 + 回调"：测试可以直接给它一个 store 与计数，
 * 不必真的开一遍 IndexedDB。
 */
export function AppSettingsScreen(): JSX.Element {
  const { store, counts, refreshCounts } = useAppSettingsStore();
  return <AppSettingsPanel store={store} counts={counts} onRefreshCounts={refreshCounts} />;
}
